import {Breadcrumbs as MuiBreadcrumbs, Link, Typography} from '@mui/material';
import {CaretRight} from '@phosphor-icons/react';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import sensorConfig, {
  slugToSensorType,
} from '../../screens/stations/StationDetail/SensorCharts/sensorConfig';

type BreadcrumbItem = {
  label: string;
  path?: string;
};

type AppBreadcrumbsProps = {
  stationName?: string;
  customItems?: BreadcrumbItem[];
};

const AppBreadcrumbs: React.FC<AppBreadcrumbsProps> = ({
  stationName,
  customItems,
}) => {
  const {formatMessage} = useIntl();
  const {pathname} = useLocation();

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const items: BreadcrumbItem[] = [];

    // Parse pathname to build breadcrumbs
    if (pathname.startsWith('/app/stations')) {
      items.push({
        label: formatMessage(messages.common.weatherStations),
        path: '/app/stations/',
      });

      if (pathname.includes('/manage')) {
        items.push({
          label: formatMessage(messages.common.modifyFavoriteStations),
        });
      } else if (pathname.includes('/search')) {
        items.push({
          label: formatMessage(messages.common.searchForLocation),
        });
      } else {
        // Check for station detail pages
        const stationMatch = pathname.match(/\/app\/stations\/(\d+)/);
        if (stationMatch) {
          if (stationName) {
            items.push({
              label: stationName,
              path: `/app/stations/${stationMatch[1]}`,
            });
          }

          if (pathname.includes('/history')) {
            items.push({
              label: 'History',
            });
          } else if (pathname.includes('/forecast')) {
            items.push({
              label: formatMessage(messages.common.forecast),
            });
          } else if (pathname.includes('/analysis')) {
            items.push({
              label: formatMessage(messages.common.analysis),
            });
          } else if (/\/sun$/.test(pathname)) {
            // Anchored, unlike its neighbours: this branch sits above
            // `/sensors/`, and a bare `includes('/sun')` would swallow any
            // future sensor slug starting with "sun" — `sunshine`,
            // `sun-duration` — leaving it with the wrong crumb and no way to
            // notice. No current slug collides; the anchor is so that adding
            // one cannot.
            //
            // The page takes the card's own name, so the crumb is the label the
            // user just clicked rather than a second name for the same thing.
            items.push({
              label: formatMessage(messages.astral.sunriseAndSunset),
            });
          } else if (/\/moon$/.test(pathname)) {
            // Anchored for the same reason as `/sun` above: a bare
            // `includes('/moon')` would swallow a future `moonlight` sensor
            // slug. Both crumbs take the label from the card the user clicked.
            items.push({label: formatMessage(messages.astral.moon)});
          } else if (pathname.includes('/sensors/')) {
            const sensorMatch = pathname.match(/\/sensors\/([^/]+)/);
            if (sensorMatch) {
              const sensorSlug = sensorMatch[1];
              const sType = slugToSensorType(sensorSlug);
              const config = sType ? sensorConfig[sType] : undefined;
              if (config) {
                const chartMatch = pathname.match(/\/sensors\/[^/]+\/(.+)/);
                items.push({
                  label: formatMessage(config.titleMessage),
                  path: chartMatch
                    ? `/app/stations/${stationMatch[1]}/sensors/${sensorSlug}`
                    : undefined,
                });
                if (chartMatch) {
                  const viz = config.visualizations.find(
                    (v) => v.chartType === chartMatch[1],
                  );
                  if (viz) {
                    items.push({
                      label: formatMessage(viz.titleMessage),
                    });
                  }
                }
              }
            }
          }
        }
      }
    } else if (pathname.startsWith('/app/settings')) {
      items.push({
        label: formatMessage(messages.settings.settings),
        path: '/app/settings/',
      });

      if (pathname.includes('/services')) {
        items.push({
          label: 'Services',
        });
      } else if (pathname.includes('/profile')) {
        items.push({
          label: formatMessage(messages.settings.profile),
        });
      } else if (pathname.includes('/units')) {
        items.push({
          label: formatMessage(messages.settings.units),
        });
      }
    } else if (pathname.startsWith('/help')) {
      items.push({
        label: formatMessage(messages.common.help),
        path: '/help',
      });
    } else if (pathname.startsWith('/map')) {
      items.push({
        label: formatMessage(messages.common.weatherMap),
      });
    }

    return items;
  };

  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length === 0) return null;

  return (
    <MuiBreadcrumbs
      separator={<CaretRight size={16} />}
      sx={{
        mb: 2,
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
        },
        '& .MuiBreadcrumbs-li': {
          whiteSpace: 'nowrap',
        },
      }}>
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;

        if (isLast || !item.path) {
          return (
            <Typography
              key={index}
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '0.875rem',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            href={item.path}
            underline="hover"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',

              '&:hover': {
                color: 'primary.main',
              },
            }}>
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default AppBreadcrumbs;
